import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

// Components
import { HeaderAcc } from "../../Components";
import SearchInfMain from "./SearchInfMain";

// Services
import {
  productService,
  searchInfluencerService,
  accountService,
  commonapiService,
} from "../../Services";

// Helpers
import { getCache, setCache } from "../../Helpers/cachedata";
import { storageHandler, utils } from "../../Helpers";
import ModalNotSubscribe from "../../Components/Base/Common/Modal/ModalNotSubscribe";

export default function SearchInf({
  setModalNotification,
  goToPricing,
  user,
  cartItem,
  closeCartPanel,
  setAuth,
  setModal,
  NotifModul,
  account,
  profiles,
  setSubsProductId,
}) {
  // Navigation & Search Params
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ADD THIS: Ref to track if initial search has been done
  const hasInitialSearchRun = useRef(false);

  // Initial values from URL
  const initialPlatform =
    searchParams.get("platform") || searchParams.get("p") || "";
  const initialCategory =
    (searchParams.get("category") || searchParams.get("c"))
      ?.split(",")
      .map((id) => ({ id })) || [];
  const initialPage = parseInt(searchParams.get("page")) || 1;

  // Toast messages
  const [toastList, setToastList] = useState([]);
  const addToast = (data) => {
    setToastList((prev) => [
      ...prev,
      { ...data, id: `${Date.now()}-${prev.length}` },
    ]);
  };

  // Data collections
  const [platform, setPlatform] = useState([]);
  const [categoriesData, setCategoriesData] = useState({});
  const [countries, setCountries] = useState([]);
  const [productList, setProductList] = useState([]);
  const [influncerList, setInfluncerList] = useState([]);
  const [paginationData, setPaginationData] = useState({});

  // User access state
  const [isUserHasSubscription, setUserHasSubscription] = useState(
    user?.data?.isUserHasSubscription === 1
  );
  const [isUserHasAccessListing, setIsUserHasAccessListing] = useState(true);

  // Filter selections
  const [selectedPlatform, setSelectedPlatform] = useState(initialPlatform);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [inputValue, setInputValue] = useState("");
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedFollowersCount, setSelectedFollowersCount] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState([]);
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedEthenicity, setSelectedEthenicity] = useState("");
  const [selectedLocationData, setSelectedLocationData] = useState({
    country: [],
    region: [],
    city: [],
  });

  // UI state
  const [startSearch, setStartSearch] = useState(false);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(12);
  const [maxpageToShow, setMaxpageToShow] = useState(3);
  const [limitedToSomePage, setLimitedToSomePage] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoadedInfluencer, setIsLoadedInfluencer] = useState(false);
  const [isCountryLoaded, setIsCountryLoaded] = useState(false);
  const [isCategoryLoaded, setIsCategoryLoaded] = useState(false);
  const [isSubscribeModal, setIsSubscribeModal] = useState(false);

  let userData = null;
  try {
    const userInfo = storageHandler.getUserInfo();
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      userData = parsed?.data || null;
    }
  } catch (error) {
    console.error("Error parsing user info:", error);
  }

  // Fetch all initial data in parallel
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [cachedCategory, cachedCountry, cachedPlatform] = [
          getCache("category"),
          getCache("country"),
          getCache("platform"),
        ];

        const [categoryRes, countryRes, configRes] = await Promise.all([
          cachedCategory || commonapiService.getCategories(),
          cachedCountry || commonapiService.getCountry(),
          cachedPlatform || commonapiService.getConfigData(),
        ]);

        // Categories
        if (!cachedCategory && categoryRes.success && categoryRes.data) {
          setCategoriesData(categoryRes.data);
          setCache("category", categoryRes.data);
        } else if (cachedCategory) {
          setCategoriesData(cachedCategory);
        }
        setIsCategoryLoaded(true);

        // Countries
        const countryData = cachedCountry || countryRes.data;
        const formattedCountries = countryData.map((item) => ({
          label: item.name,
          value: item.id,
        }));
        setCountries(formattedCountries);
        if (!cachedCountry) setCache("country", countryRes.data);
        setIsCountryLoaded(true);

        // Platform Config
        const platformData = cachedPlatform || configRes.socialMedia;
        const formattedPlatform = [{ id: "Any", name: "Any" }, ...platformData];
        setPlatform(formattedPlatform);
        if (!cachedPlatform) setCache("platform", platformData);
        setIsLoaded(true);
      } catch (err) {
        addToast({ error: true, msg: err.message });
      }
    };

    fetchInitialData();
  }, []);

  // MODIFIED: Apply URL param filters once platform and categories are loaded
  useEffect(() => {
    // Only run once when all data is loaded and haven't run initial search yet
    if (!isLoaded || !isCountryLoaded || !isCategoryLoaded || hasInitialSearchRun.current) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const platformParam = searchParams.get("platform") || searchParams.get("p");
    const categoryParam = searchParams.get("category") || searchParams.get("c");

    let didChange = false;

    if (platformParam && platform?.length > 0) {
      const matchedPlatform = platform.find(
        (p) => p.id.toString() === platformParam
      );
      if (matchedPlatform) {
        changeSelectedPlatform(matchedPlatform);
        didChange = true;
      }
    }

    if (categoryParam && Array.isArray(categoriesData)) {
      const categoryIds = categoryParam
        .split(",")
        .map((id) => parseInt(id, 10));
      const matchedCategories = categoriesData.filter((cat) =>
        categoryIds.includes(cat.id)
      );
      if (matchedCategories.length) {
        setSelectedCategory(matchedCategories);
        didChange = true;
      }
    }

    // Mark that we've run the initial search
    hasInitialSearchRun.current = true;

    if (didChange) {
      // Remove the used params from the URL
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete("platform");
      newSearchParams.delete("p");
      newSearchParams.delete("category");
      newSearchParams.delete("c");
      newSearchParams.delete("page");
      navigate(
        { pathname: location.pathname, search: newSearchParams.toString() },
        { replace: true }
      );
    }
    
    // Trigger initial search only once
    setStartSearch(true);
  }, [
    platform,
    categoriesData,
    isLoaded,
    isCountryLoaded,
    isCategoryLoaded,
  ]);

  useEffect(() => {
    const paypalError =
      searchParams.get("error") === "1"
        ? "We're having trouble connecting to your payment account. Please try again."
        : "";
    const successMessage =
      searchParams.get("success") === "1"
        ? "Your selected plan has been successfully subscribed."
        : "";

    if (successMessage) {
      accountService.refreshToken(() => {});
      addToast({ error: false, msg: successMessage });
    }
    if (paypalError) {
      addToast({ error: true, msg: paypalError });
    }
  }, []);

  useEffect(() => {
    setUserHasSubscription(user?.data?.isUserHasSubscription === 1);
  }, [user]);

  // MODIFIED: Fetch influencer data based on filters and page
  useEffect(() => {
    // Don't fetch if initial search hasn't run yet
    if (!hasInitialSearchRun.current) {
      return;
    }

    const fetchInfluencers = async () => {
      const locationIds = countries
        .filter((c) => selectedLocationData.country.includes(c.label))
        .map((c) => c.value);

      // Priority: text input first, then selected categories
      const categoryValue = inputValue.trim() 
        ? inputValue.trim()
        : selectedCategory.map(cat => 
            cat.type === 'dropdown' ? cat.id : cat.name
          ).join(",");
      
      const genderCode =
        selectedGender === "Male"
          ? "m"
          : selectedGender === "Female"
          ? "f"
          : "o";

      const options = {
        platform: selectedPlatform,
        category: categoryValue,
        product: selectedProduct,
        followers: selectedFollowersCount,
        location: locationIds,
        price: selectedPrice,
        gender: selectedGender ? genderCode : "",
        age: selectedAge,
        ethenicity: selectedEthenicity,
        language: selectedLanguage,
        page,
        pageSize,
      };

      try {
        const res = await searchInfluencerService.searchInfluencer(
          options,
          profiles
        );
        if (res.success) {
          setInfluncerList(res.data.data);
          setPaginationData(utils.createPaginationData(res.data));
        }
      } catch (err) {
        const message =
          typeof err === "string" ? err.replace(/<[^>]*>/g, "") : err.message;
        addToast({ error: true, msg: message });
      } finally {
        setIsLoadedInfluencer(true);
      }
    };

    fetchInfluencers();
  }, [startSearch, page]); // Added page dependency

  async function fetchProductData(platform) {
    try {
      const res = await productService.getAllPlatform();
      const prd = res.data.filter((item) => item.social_media_id === platform);
      setProductList(platform !== "Any" ? prd : []);
    } catch (err) {
      addToast({ error: true, msg: err });
    }
  }

  const changeSelectedPlatform = (item) => {
    setSelectedProduct([]);
    setSelectedPlatform(item.id);
    fetchProductData(item.id);
  };

  const onPaginate = (newPage) => {
    console.log("🚀 ~ onPaginate ~ newPage:", newPage);
    // if (profiles) {
    //   if (
    //     isUserHasSubscription ||
    //     newPage <= maxpageToShow ||
    //     userData.p_f_ids.includes(1)
    //   ) {
    //     setPage(newPage);
    //   } else {
    //     setLimitedToSomePage(true);
    //   }
    // } else if (newPage <= maxpageToShow) {
    //   setPage(newPage);
    // } else {
    //   navigate("/account/pricing");
    // }

    if (newPage <= maxpageToShow) {
      setPage(newPage);
    } else if (profiles) {
      if (isUserHasSubscription && userData.p_f_ids.includes(1)) {
        console.log("here");
        setPage(newPage);
      } else {
        setIsSubscribeModal(true);
      }
    } else {
      navigate("/account/pricing");
    }
  };

  const clearAll = () => {
    setSelectedPlatform("");
    setSelectedCategory([]);
    setInputValue("");
    setSelectedProduct([]);
    setSelectedAge("");
    setSelectedFollowersCount([]);
    setSelectedPrice([]);
    setSelectedLanguage("");
    setSelectedGender("");
    setSelectedEthenicity("");
    setSelectedLocationData({ country: [], region: [], city: [] });
    setStartSearch(!startSearch);
  };

  return (
    <>
      {profiles ? (
        <div className="main">
          <div className="main__inner">
            {isCountryLoaded && isCategoryLoaded && isLoaded && (
              <SearchInfMain
                {...{
                  selectedPlatform,
                  selectedProduct,
                  setSelectedAge,
                  setSelectedFollowersCount,
                  setSelectedPrice,
                  setSelectedProduct,
                  productList,
                  selectedCategory,
                  inputValue,
                  setInputValue,
                  changeSelectedPlatform,
                  platform,
                  selectedAge,
                  setSelectedEthenicity,
                  setSelectedLanguage,
                  categoriesData,
                  clearAll,
                  selectedGender,
                  setSelectedGender,
                  isUserHasSubscription,
                  selectedLanguage,
                  selectedEthenicity,
                  countries,
                  setSelectedLocationData,
                  selectedLocationData,
                  setSelectedCategory,
                  setStartSearch,
                  startSearch,
                  profiles,
                  influncerList,
                  selectedFollowersCount,
                  selectedPrice,
                  paginationData,
                  onPaginate,
                  setPage,
                  isLoadedInfluencer,
                  setIsUserHasAccessListing,
                  goToPricing,
                  setModalNotification,
                  goToPricing,
                  user,
                  cartItem,
                  closeCartPanel,
                  setAuth,
                  setModal,
                  NotifModul,
                  account,
                  profiles,
                  setSubsProductId,
                }}
              />
            )}
          </div>
        </div>
      ) : (
        isCountryLoaded &&
        isCategoryLoaded &&
        isLoaded && (
          <div className="searchouterpage">
            <HeaderAcc />
            <div
              className="account__inner"
              style={{ margin: "0 auto", width: "fit-content" }}
            >
              <div className="account__inner-col-image">
                <div className="showSearchFilterAndResult">
                  <SearchInfMain
                    {...{
                      selectedPlatform,
                      selectedProduct,
                      setSelectedAge,
                      selectedCategory,
                      inputValue,
                      setInputValue,
                      setSelectedFollowersCount,
                      setSelectedPrice,
                      setSelectedProduct,
                      productList,
                      changeSelectedPlatform,
                      platform,
                      selectedAge,
                      setSelectedEthenicity,
                      setSelectedLanguage,
                      categoriesData,
                      clearAll,
                      selectedGender,
                      setSelectedGender,
                      isUserHasSubscription,
                      selectedLanguage,
                      selectedEthenicity,
                      countries,
                      setSelectedLocationData,
                      selectedLocationData,
                      setSelectedCategory,
                      setStartSearch,
                      startSearch,
                      setPage,
                      isLoadedInfluencer,
                      profiles,
                      influncerList,
                      selectedFollowersCount,
                      selectedPrice,
                      paginationData,
                      onPaginate,
                      goToPricing,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {isSubscribeModal && (
        <ModalNotSubscribe
          type="brand"
          title={"Direct Campaigns"}
          onClose={() => setIsSubscribeModal(false)}
          goToPricing={goToPricing}
        ></ModalNotSubscribe>
      )}
    </>
  );
}